import type { BackLocation } from '@/utils/back-navigation.utils'
import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router'

export type NavigationAction = 'POP' | 'PUSH' | 'REPLACE'

const trail: BackLocation[] = []
let backNavigation = false

const normalizePath = (pathname: string) => pathname.replace(/\/+$/, '') || '/'

const lastIndexOfPath = (pathname: string) => {
  for (let index = trail.length - 1; index >= 0; index -= 1) {
    if (trail[index]?.pathname === pathname) return index
  }
  return -1
}

const applyBack = (entry: BackLocation) => {
  const index = lastIndexOfPath(entry.pathname)
  if (index >= 0) {
    trail.splice(index + 1)
    trail[index] = entry
    return
  }

  if (trail.length > 0) trail.pop()
  const last = trail[trail.length - 1]
  if (!last || last.pathname !== entry.pathname) trail.push(entry)
  else last.search = entry.search
}

/** Drop in-memory history. Test-only. */
export const resetNavigationTrail = () => {
  trail.length = 0
  backNavigation = false
}

export const previousTrailEntry = (): BackLocation | null => {
  if (trail.length < 2) return null
  const entry = trail[trail.length - 2]
  return entry ? { pathname: entry.pathname, search: entry.search } : null
}

/** Call immediately before navigating back so the next location replaces history instead of appending. */
export const beginBackNavigation = () => {
  backNavigation = true
}

export const recordNavigation = (pathname: string, search: string, action: NavigationAction) => {
  const entry = { pathname: normalizePath(pathname), search }
  const goingBack = backNavigation
  backNavigation = false

  if (goingBack || action === 'POP') {
    applyBack(entry)
    return
  }

  if (action === 'REPLACE') {
    if (trail.length === 0) trail.push(entry)
    else trail[trail.length - 1] = entry
    return
  }

  const last = trail[trail.length - 1]
  if (!last) {
    trail.push(entry)
    return
  }
  if (last.pathname === entry.pathname) {
    last.search = entry.search
    return
  }
  trail.push(entry)
}

/** Records dashboard navigations so back can replay them without the browser history stack. */
export const useNavigationTrail = () => {
  const location = useLocation()
  const navigationType = useNavigationType()

  useEffect(() => {
    recordNavigation(location.pathname, location.search, navigationType)
  }, [location.pathname, location.search, navigationType])
}
